import type { MouseEvent } from 'react';
import { Space } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import type { WrappedResult } from '@/api/types';
import { DeleteAction, DownloadAction } from '@/components/table/actions';
import { PERMISSION_DELETE, PERMISSION_OBJ_ATTACHMENT } from '@/constants/permissions';
import { withPermissions } from '@/hocs/permission';
import { attachmentHistoryStore } from '@/stores';
import { formatBytes, showError } from '@/utils/common';
import { formatMimeTypeLabel } from '@/utils/mime';
import { getFormattedDate } from '@/utils/moment';

const PermissionedDeleteAction = withPermissions([[PERMISSION_OBJ_ATTACHMENT, PERMISSION_DELETE]])(DeleteAction);

const formatAttachmentType = (type: cyrian.api.attachments.AttachmentType) => (
  <FormattedMessage id={`attachment-history.attachment-type.${type}`} defaultMessage={type} />
);

const formatObjectRef = (_: unknown, record: cyrian.api.attachments.Attachment) =>
  record.objectId != null ? `#${record.objectId}` : '—';

const Actions = (_: any, record: cyrian.api.attachments.Attachment) => {
  const onDownload = (e: MouseEvent) => {
    e.stopPropagation();
    api.attachments
      .get(record.key)
      .source.then((body: WrappedResult<{ url: string }>) => {
        if (body?.data?.url) {
          window.open(body.data.url, '_blank');
        }
      })
      .catch(showError);
  };

  const onDelete = (e: MouseEvent) => {
    e.stopPropagation();
    api.attachments
      .delete(record.id)
      .source.then(() => attachmentHistoryStore.deleteItem(record.id))
      .catch(showError);
  };

  return (
    <Space>
      <DownloadAction onClick={onDownload} />
      <PermissionedDeleteAction onConfirm={onDelete} />
    </Space>
  );
};

export const columns: ColumnType<cyrian.api.attachments.Attachment>[] = [
  {
    title: <FormattedMessage id="attachment-history.name" />,
    dataIndex: 'name',
    sorter: true,
    render: (value: string) => value ?? '—',
  },
  {
    title: <FormattedMessage id="attachment-history.type" />,
    dataIndex: 'type',
    sorter: true,
    width: 180,
    render: (value: cyrian.api.attachments.AttachmentType | undefined) => (value ? formatAttachmentType(value) : '—'),
  },
  {
    title: <FormattedMessage id="attachment-history.object" />,
    key: 'object',
    width: 120,
    render: formatObjectRef,
  },
  {
    title: <FormattedMessage id="attachment-history.size" />,
    dataIndex: 'size',
    sorter: true,
    width: 110,
    render: (value: number) => (value != null ? formatBytes(value) : '—'),
  },
  {
    title: <FormattedMessage id="attachment-history.mimetype" />,
    dataIndex: 'mimetype',
    width: 90,
    render: (value: string | null) => formatMimeTypeLabel(value),
  },
  {
    title: <FormattedMessage id="attachment-history.uploadedBy" />,
    dataIndex: 'uploadedByEmail',
    render: (value: string | null) => value ?? '—',
  },
  {
    title: <FormattedMessage id="attachment-history.createdAt" />,
    dataIndex: 'createdAt',
    sorter: true,
    defaultSortOrder: 'descend',
    width: 170,
    render: getFormattedDate,
  },
  {
    title: '',
    render: Actions,
    className: 'actions',
  },
];
